/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { articleCategoryLabelKey } from '../../lib/kennisbankIndex.js';

// The block types are ONE definition in src/types/api/contentBlocks.ts
// (type-only, so the server can read them too); re-exported here for every
// editor and renderer that imports them from this module.
import type {
  ArticleContentBlock,
  CalloutContentBlock,
  ContentBlockKind,
  DividerContentBlock,
  FAQContentBlock,
  FAQItem,
  ImageContentBlock,
  QuoteContentBlock,
  RichContentBlock,
  TableBorders,
  TableCaptionPosition,
  TableColumnAlignment,
  TableContentBlock,
  TableDensity,
  TableHeaderStyle,
} from '../../types/api/contentBlocks';

export type {
  ArticleContentBlock,
  ArticleCTA,
  CalloutContentBlock,
  CalloutVariant,
  ContentBlockKind,
  DividerContentBlock,
  DividerStyle,
  FAQContentBlock,
  FAQItem,
  ImageAlignment,
  ImageContentBlock,
  ImageWidth,
  QuoteContentBlock,
  QuoteVariant,
  RichContentBlock,
  RichImageAlignment,
  TableBorders,
  TableCaptionPosition,
  TableColumnAlignment,
  TableContentBlock,
  TableDensity,
  TableHeaderStyle,
} from '../../types/api/contentBlocks';

export interface ResolvedTableStyle {
  density: TableDensity;
  borders: TableBorders;
  striped: boolean;
  headerStyle: TableHeaderStyle;
  captionPosition: TableCaptionPosition;
  hasHeader: boolean;
  columnAlignments: TableColumnAlignment[];
}

// Centralised defaults. Old articles render byte-identical when these are
// the chosen values for every undefined field.
export function resolveTableStyle(block: TableContentBlock): ResolvedTableStyle {
  const hasHeader = block.hasHeaderRow !== false;
  const colCount = block.rows[0]?.length ?? 0;
  const cols =
    block.columnAlignments && block.columnAlignments.length > 0
      ? block.columnAlignments
      : ([] as TableColumnAlignment[]);
  const columnAlignments: TableColumnAlignment[] = Array.from(
    { length: colCount },
    (_, i) => cols[i] ?? 'left'
  );
  return {
    density: block.density ?? 'cozy',
    borders: block.borders ?? 'horizontal',
    striped: block.striped ?? true,
    // headerStyle defaults to 'top' iff a header row is present; otherwise 'none'.
    headerStyle: block.headerStyle ?? (hasHeader ? 'top' : 'none'),
    captionPosition: block.captionPosition ?? 'bottom',
    hasHeader,
    columnAlignments,
  };
}

export function isFAQBlock(b: ArticleContentBlock): b is FAQContentBlock {
  return b.kind === 'faq';
}

export function isTableBlock(b: ArticleContentBlock): b is TableContentBlock {
  return b.kind === 'table';
}

export function isCalloutBlock(b: ArticleContentBlock): b is CalloutContentBlock {
  return b.kind === 'callout';
}

export function isQuoteBlock(b: ArticleContentBlock): b is QuoteContentBlock {
  return b.kind === 'quote';
}

export function isDividerBlock(b: ArticleContentBlock): b is DividerContentBlock {
  return b.kind === 'divider';
}

export function isImageBlock(b: ArticleContentBlock): b is ImageContentBlock {
  return b.kind === 'image';
}

// Rich is the default kind — legacy blocks have no `kind` field at all.
export function isRichBlock(b: ArticleContentBlock): b is RichContentBlock {
  return !b.kind || b.kind === 'rich';
}

export interface ArticleTranslation {
  title: string;
  excerpt: string;
  blocks: ArticleContentBlock[];
}

export interface Article {
  id: string;
  // Primary slug. Doubles as the Dutch slug AND the universal fallback: when
  // `slugEn` is empty, the English URL reuses this value. This keeps every
  // pre-existing article (which only has `slug`) working with no migration.
  slug: string;
  // Optional English-specific slug. When set, it localises the
  // /en/kennisbank/<slug> URL independently of the Dutch one. When empty,
  // English falls back to `slug`. Resolve via effectiveArticleSlug
  // (src/lib/articleSlug.js) — the rule the API, the bake and the head use.
  slugEn?: string;
  heroImage?: string;
  // Where the hero is cropped, stored like the provider's `photoFocus`
  // ("<x>% <y>%", see src/lib/imageFocus.ts); '' or absent = centre. Always
  // belongs to the current `heroImage` — a new image clears it.
  heroImageFocus?: string;
  published: boolean;
  showToc?: boolean;
  // Admin username (matches users.json). When set, the public article + index
  // endpoints attach the corresponding profile as `author`. Optional so legacy
  // articles render without a byline.
  authorUsername?: string;
  // One of ARTICLE_CATEGORIES in src/lib/kennisbankIndex.js; the chip the
  // article is listed under on /kennisbank. Anything else (or nothing) reads
  // as "no category": the article then appears under "Alle" only.
  category?: string;
  createdAt: string;
  updatedAt: string;
  // The old manual reading order. Nothing reads it since 2026-09-23 — the
  // kennisbank is newest first by `createdAt` — and nothing writes it for a
  // new article since 2026-10-14. Older records keep their value (a save
  // passes it through), so no data migration is needed.
  order?: number;
  nl: ArticleTranslation;
  en: ArticleTranslation;
}

/**
 * The patch that points an article's hero at `path` — the ONE place the editor
 * changes `heroImage` (library pick, typed path, remove button). A NEW image
 * resets the position: a crop tuned for the old picture would cut an arbitrary
 * corner out of the new one, and an emptied path takes it away too. Handing in
 * the path the article already holds keeps the position, so a no-op pick can
 * not undo an hour of dragging (same rule as assignSlotImage in slotImages.ts).
 * `heroImageFocus: undefined` drops out of the JSON body, which is how the
 * server's scoped save learns the position is gone.
 */
export function heroImagePatch(
  article: Pick<Article, 'heroImage'>,
  path: string
): Pick<Article, 'heroImage' | 'heroImageFocus'> {
  if ((article.heroImage ?? '') === path) return { heroImage: path };
  return { heroImage: path, heroImageFocus: undefined };
}

/**
 * The public label of an article category, for the admin: the site's own copy
 * (the key of `articleCategoryLabelKey`, `kennisbank.category_<id>_v2`) through
 * the site translator, the same `t()` the admin sidebar labels itself with.
 * Not the admin's `content`: the Kennisblogger tab loads articles only, so that
 * is null when the admin lands there. Falls back to the bare id when the key is
 * missing (`t()` then echoes the key path).
 */
export function articleCategoryLabel(t: (key: string) => unknown, id: string): string {
  const key = articleCategoryLabelKey(id);
  const label = t(key);
  return typeof label === 'string' && label.trim() && label !== key ? label : id;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function createEmptyArticle(): Article {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    slug: '',
    published: false,
    createdAt: now,
    updatedAt: now,
    nl: { title: '', excerpt: '', blocks: [] },
    en: { title: '', excerpt: '', blocks: [] },
  };
}

export function createEmptyBlock(kind: ContentBlockKind = 'rich'): ArticleContentBlock {
  const id = crypto.randomUUID();
  switch (kind) {
    case 'faq':
      return {
        id,
        kind: 'faq',
        heading: '',
        items: [createEmptyFAQItem()],
        includeInSchema: true,
      };
    case 'table':
      return {
        id,
        kind: 'table',
        heading: '',
        hasHeaderRow: true,
        rows: [
          ['', ''],
          ['', ''],
          ['', ''],
        ],
      };
    case 'callout':
      return {
        id,
        kind: 'callout',
        variant: 'info',
        heading: '',
        text: '',
      };
    case 'quote':
      return {
        id,
        kind: 'quote',
        text: '',
        attribution: '',
        variant: 'plain',
      };
    case 'divider':
      return {
        id,
        kind: 'divider',
        style: 'line',
      };
    case 'image':
      return {
        id,
        kind: 'image',
        src: '',
        alt: '',
        caption: '',
        alignment: 'center',
        width: 'lg',
      };
    case 'rich':
    default:
      return {
        id,
        kind: 'rich',
        heading: '',
        text: '',
        cta: null,
      };
  }
}

export function duplicateBlock(block: ArticleContentBlock): ArticleContentBlock {
  // Deep clone via JSON round-trip; safe because blocks are pure data.
  const cloned = JSON.parse(JSON.stringify(block)) as ArticleContentBlock;
  cloned.id = crypto.randomUUID();
  // Refresh nested IDs (FAQ items) so anchors stay unique.
  if (isFAQBlock(cloned)) {
    cloned.items = cloned.items.map((item) => ({ ...item, id: crypto.randomUUID() }));
  }
  return cloned;
}

export function createEmptyFAQItem(): FAQItem {
  return { id: crypto.randomUUID(), q: '', a: '' };
}

/** Block kinds whose `heading` is a *section* heading: rendered as the `<h2>`
 * above the block, listed in the table of contents, edited via the generic
 * "Koptekst" input. Callouts are deliberately NOT in this set — their
 * `heading` is the title *inside* the coloured box (an aside, not a section),
 * edited via the callout editor's own "Titel" field. Keeping the two apart is
 * what stops the editor from showing one field twice and the TOC from linking
 * to anchors that don't exist. */
export type SectionHeadingBlock = RichContentBlock | FAQContentBlock | TableContentBlock;

export function hasSectionHeading(b: ArticleContentBlock): b is SectionHeadingBlock {
  return isRichBlock(b) || isFAQBlock(b) || isTableBlock(b);
}

/** The section heading of a block, or `undefined` for kinds without one. */
export function getSectionHeading(b: ArticleContentBlock): string | undefined {
  return hasSectionHeading(b) ? b.heading : undefined;
}

// Stable, URL-safe DOM id for a block's heading. Used both as the `<h2 id>`
// target and as the TOC anchor href. The `id` suffix guarantees uniqueness
// even if two blocks share the exact same heading text.
export function blockHeadingId(block: ArticleContentBlock): string {
  const heading = getSectionHeading(block);
  const base = heading ? slugify(heading) : '';
  const suffix = block.id.slice(0, 8);
  return base ? `${base}-${suffix}` : `section-${suffix}`;
}

// Human-readable preview for collapsed block headers. Used by BlockShell.
export function blockPreviewText(block: ArticleContentBlock, maxLength = 120): string {
  const truncate = (s: string) => {
    const clean = s
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return clean.length > maxLength ? `${clean.slice(0, maxLength).trimEnd()}…` : clean;
  };
  if (isRichBlock(block)) return truncate(block.text);
  if (isFAQBlock(block)) return truncate(block.items[0]?.q ?? '');
  if (isTableBlock(block)) return truncate(block.rows[0]?.join(' · ') ?? '');
  if (isCalloutBlock(block)) return truncate(block.text);
  if (isQuoteBlock(block)) return truncate(block.text);
  if (isDividerBlock(block)) return '';
  if (isImageBlock(block)) return block.caption ?? block.alt ?? '';
  return '';
}
